import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Component, computed, input, numberAttribute} from "@angular/core";
import {ChartConfiguration} from "c3";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {C3GrafKomponent} from "../c3-graf/c3-graf";
import {nil} from "felles-ng2/typer/nullish";

const debug = initDebugger('C3TodeltPaiKomponent');

/** Viser en graf vhja biblioteket 'c3', se https://c3js.org */
@Component({
    selector: 'c3-todelt-pai',
    template: `
        <c3-graf [konfigurasjon]="c3Konfigurasjon()" bredde="100%" hoeyde="100%"/>
        <ng-content>
            @if (visningstype() == "donut" && totalt() != null) {
                <!-- setter fontstørrelsen til et produkt av angitt bredde, ved std bredde=22px gir dette en fontstørrelse på 8px -->
                <span class="donut-tittel" [style.font-size.px]="bredde()/2.75">{{ andel() }}/{{ totalt() }}</span>
            }
        </ng-content>
        `,
    styleUrl: 'c3-todelt-pai.scss',
    host: {
        '[class]': 'visningstype()',
        '[style.width.px]': 'bredde()',
        '[style.height.px]': 'bredde()',
        '[style.border-color]': 'rammefarge()'
    },
    imports: [C3GrafKomponent]
})
export class C3TodeltPaiKomponent {

    andel = input.required<number | nil>();
    totalt = input.required<number | nil>();
    konfig = input<Partial<C3TodeltPaiKonfig>>({});
    /** Bredde og høyde på paien, målt i pixler */
    bredde = input(22, {transform: numberAttribute});
    tooltip = input<TooltipTekst | null>(null);
    visningstype = input<Diagramtype>('pie');

    konfigMedOverstyringer = computed(() => this.beregnKonfigMedOverstyringer());
    rammefarge = computed(() => this.beregnRammefarge());
    c3Konfigurasjon = computed(() => this.beregnC3Konfigurasjon());

    constructor() {
    }

    //hjelpemetoder

    /** Farge på ramme rundt pie, kun aktuell dersom visningstype=pie */
    private beregnRammefarge(): string {
        if (erNil(this.totalt()) || this.totalt() == 0)
            return this.konfigMedOverstyringer().andelFarge;

        switch (this.andel()) {
            case null:
                return this.konfigMedOverstyringer().andelFarge;
            case 0:
                return this.konfigMedOverstyringer().ikkePåbegyntPieBorderFarge;
            case this.totalt():
                return this.konfigMedOverstyringer().fullførtFarge;
            default:
                return this.konfigMedOverstyringer().andelFarge;
        }
    }

    private beregnKonfigMedOverstyringer(): C3TodeltPaiKonfig {
        return this.visningstype() == 'pie' ?
          //standard verdier for visningstype=pie
          {
              ikkePåbegyntFarge: '#db7b7b',
              ikkePåbegyntPieBorderFarge: '#db7b7b',
              fullførtFarge: '#71a371',
              restFarge: '#fff',
              andelFarge: '#6a6a6a',
              interaksjonAktivert: true,
              ...this.konfig()
          } :
          //standard verdier for visningstype=donut
          {
              ikkePåbegyntPieBorderFarge: '#fff', //ikke relevant for donut
              ikkePåbegyntFarge: '#ddd',
              fullførtFarge: '#71a371',
              restFarge: '#ddd',
              andelFarge: '#71a371',
              interaksjonAktivert: true,
              strektykkelse: 2,
              ...this.konfig()
          };
    }

    /** C3 konfigurasjon, se https://c3js.org/reference.html */
    private beregnC3Konfigurasjon(): ChartConfiguration {
        //for at graf skal dekke forespurt bredde (this.bredde) må vi regne om litt - +11 er testet med bredde=22 og 50
        const c3Bredde = this.bredde() + 11;
        let andel = this.andel() ?? 0;
        let totalt = this.totalt() ?? 0;
        let rest = totalt - andel;
        //for at vi skal vise en 'sluttet ring' når totalt = 0 og vi skal vise donut, må vi sette rest til > 0
        if (this.visningstype() == 'donut' && totalt == 0) rest = 1;

        return {
            data: {
                columns: [
                    ['andel', andel],
                    ['rest', rest],
                ],
                order: null,
                type: this.visningstype(),
                colors: {
                    //andel: totalt-andel == 0 ? '#71a371' : '#a3e0a3',
                    andel: rest == 0 ? this.konfigMedOverstyringer().fullførtFarge : this.konfigMedOverstyringer().andelFarge,
                    //rest: andel == 0 ? '#db7b7b' : '#d8adad',
                    rest: andel == 0 ? this.konfigMedOverstyringer().ikkePåbegyntFarge : this.konfigMedOverstyringer().restFarge
                }
            },
            size: {
                height: c3Bredde,
                width: c3Bredde
            },
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            },
            legend: {show: false},
            tooltip: {
                show: erIkkeNil(this.tooltip()),
                contents: (data, defaultTitleFormat, defaultValueFormat, color) => {
                    return `<div class="c3-todelt-pie-tooltip">${this.tooltip()!(data[0].id, data[0].value)}</div>`;
                }
            },
            interaction: {
                enabled: this.konfigMedOverstyringer().interaksjonAktivert
            },
            //kun effekt når type='pie'
            pie: {
                label: {show: false}
            },
            //kun effekt når type='donut'
            donut: {
                label: {show: false},
                width: this.konfigMedOverstyringer().strektykkelse //verdi mindre enn 2 fører til at ingen strek vises..
            },
        }
    }
}

export type Diagramtype = 'pie' | 'donut';

export type DonutTittel = (andel: number, totalt: number) => string;

export interface C3TodeltPaiKonfig {
    /** farge på ramme rundt pie nå andel=0 og totalt>0, kun aktuell dersom visningstype=pie */
    ikkePåbegyntPieBorderFarge: string;
    /** farge på segment som vises når andel=0 */
    ikkePåbegyntFarge: string;
    /** farge på segment som vises når andel=totalt */
    fullførtFarge: string;
    /** farge på segment som representerer rest (totalt-andel) */
    restFarge: string;
    /** farge på segment som representerer andel */
    andelFarge: string;
    /** dersom sann vises verdi for segment man holder musepeker over */
    interaksjonAktivert: boolean;
    /** Tykkelse på donut-streken som representere hhv andel- og totalt-verdien. Minimum 2 */
    strektykkelse?: number;
    /** Dersom angitt: antall millis vi skal vente fra angulars $onDestroy kalles til vi kaller pieInstans.destroy().
     * Kan benyttes feks til å vente med å skjule pie til animasjon er ferdig. */
    termineringsTimeout?: number;
}

export type TooltipTekst = (kolonneId: string, verdi: number) => string;
